import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextArea,
  Button,
  DeprecatedSelect,
  Flex,
  Text,
} from '@fullstory/components';
import { BasePlan, PlanStage, PlanConfig } from '@fullstory/proto/fs/services/orgs';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppState,
  SettingsRequestActions,
  AppDispatch,
  fullstorySelectors,
} from '@fullstory/redux';
import { PlanChangeRequest } from '@fullstory/proto/fs/api/clientapi';
import { getBrowserApi } from '@fullstory/browser-api';
import { Footer } from '../ContactFooter';
import css from './index.module.scss';
import { useNotifications, useXhr, useFlags } from '@fullstory/react-hooks';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { Flag } from '@fullstory/proto/fs/shared/flags';
const { getOrgId } = fullstorySelectors;
const REQUEST_PLAN_CHANGE_ENDPOINT = '/requestPlanChange';

interface ContactReason {
  id: ReasonId;
  name: string;
  includeReason: (current: PlanConfig) => boolean; // Return true if this option should be included base on current plan
}

enum ReasonId {
  Business = 'upgrade_business',
  Advanced = 'upgrade_advanced',
  Enterprise = 'upgrade_enterprise',
  UpgradePlan = 'upgrade_plan',
  AccountChange = 'account_change',
  GetInsights = 'get_insights',
  Other = 'other',
  // TODO(sarahgrace): Old reasons, remove once fully migrated
  Quota = 'increase_quota',
  Seats = 'add_seats',
  Anywhere = 'add_anywhere',
  DataDestinations = 'add_data_destinations', // TODO: remove when migrated
  MobileApps = 'mobile_apps',
}

interface ContactUsProps {
  closeFn: () => void;
  onSend: () => void; // Callback for when a contact message is sent.
  initialReasonId: ReasonId;
  showFreeLink?: boolean; // Whether to show the pricing link or the Go Free link in the footer.
  isUpgradePage?: boolean; // Whether to show options specifically for upgrading
}

const useSettings = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, data } = useSelector((state: AppState) => state.settings.settings);

  useEffect(() => {
    dispatch(SettingsRequestActions.getSettings());
  }, [dispatch]);

  return { loading, error, data };
};

const ContactUs: React.FunctionComponent<ContactUsProps> = ({
  onSend,
  initialReasonId,
  showFreeLink,
  isUpgradePage = false,
}) => {
  const notifications = useNotifications();
  const xhr = useXhr();
  const settings = useSettings().data;
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const isWarehousingEnabled = flags.hasBit(Flag.FeatWarehousing); // True if the customer has purchased data destinations; otherwise, false
  const showAnywhere = flags.featureFlags().AppFsAnywhereCallout;

  // Will be undefined if free link is disabled or user does not have modify perms.
  const freePath =
    showFreeLink &&
    flags?.hasBit(Flag.PermModifyBilling) &&
    'https://www.fullstory.com/signup/?freemium';

  const reasons: ContactReason[] = useMemo(
    () => [
      {
        id: ReasonId.UpgradePlan,
        name: intl.formatMessage({
          defaultMessage: 'Upgrade my plan',
          id: '0n/fqq',
        }),
        includeReason: (current: PlanConfig) => isUpgradePage,
      },
      {
        id: ReasonId.AccountChange,
        name: intl.formatMessage({
          defaultMessage: 'Update sessions, seats or monthly retention',
          id: 'x0d550',
        }),
        includeReason: (current: PlanConfig) => true,
      },
      {
        id: ReasonId.Business,
        name: intl.formatMessage({ defaultMessage: 'Upgrade to Business', id: '4+9X8E' }),
        includeReason: (current: PlanConfig) => {
          if (isUpgradePage) {
            return false;
          }
          if (current.planStage === PlanStage.ACTIVE_STAGE) {
            return (
              current.basePlan !== BasePlan.BUSINESS_PLAN &&
              current.basePlan !== BasePlan.ADVANCED_PLAN &&
              current.basePlan !== BasePlan.ENTERPRISE_PLAN
            );
          }
          return true; // i.e. trial
        },
      },
      {
        id: ReasonId.Advanced,
        name: intl.formatMessage({ defaultMessage: 'Upgrade to Advanced', id: '0qxb2p' }),
        includeReason: (current: PlanConfig) => {
          if (isUpgradePage) {
            return false;
          }
          if (current.planStage === PlanStage.ACTIVE_STAGE) {
            return (
              current.basePlan !== BasePlan.ADVANCED_PLAN &&
              current.basePlan !== BasePlan.ENTERPRISE_PLAN
            );
          }
          return true; // i.e. trial
        },
      },
      {
        id: ReasonId.Enterprise,
        name: intl.formatMessage({ defaultMessage: 'Upgrade to Enterprise', id: 'EF0lZH' }),
        includeReason: (current: PlanConfig) => {
          if (isUpgradePage) {
            return false;
          }
          if (current.planStage === PlanStage.ACTIVE_STAGE) {
            return current.basePlan !== BasePlan.ENTERPRISE_PLAN;
          }
          return true; // i.e. trial
        },
      },
      {
        id: ReasonId.MobileApps,
        name: intl.formatMessage({
          defaultMessage: 'Get Fullstory for mobile apps',
          id: 'vyliQT',
        }),
        includeReason: (current: PlanConfig) => isUpgradePage,
      },
      showAnywhere
        ? {
            id: ReasonId.Anywhere,
            name: intl.formatMessage({ defaultMessage: 'Get Anywhere', id: '5z2oRw' }),
            includeReason: (current: PlanConfig) => isUpgradePage || !isWarehousingEnabled,
          }
        : {
            id: ReasonId.DataDestinations,
            name: intl.formatMessage({
              defaultMessage: 'Get Destinations',
              id: 'Yv5+6R',
            }),
            includeReason: (current: PlanConfig) => isUpgradePage || !isWarehousingEnabled,
          },
      {
        id: ReasonId.Other,
        name: intl.formatMessage({ defaultMessage: 'Discuss the right plan for me', id: 'jNuYm1' }),
        includeReason: (current: PlanConfig) => !isUpgradePage,
      },
      {
        id: ReasonId.GetInsights,
        name: intl.formatMessage({
          defaultMessage: 'Get Insights+',
          id: 'k4mVMp',
        }),
        includeReason: (current: PlanConfig) => isUpgradePage,
      },
    ],
    [isUpgradePage, isWarehousingEnabled, showAnywhere],
  );

  const reason = useMemo(
    () => reasons.find(r => r.id == initialReasonId) || reasons[0]!,
    [initialReasonId, reasons],
  );

  // when hasEvents changes, we want to reset the contact reason to get the updated text
  useEffect(() => {
    setContactReason(reason);
  }, [reason]);

  const [sendDisabled, setSendDisabled] = useState(false);
  const [contactReason, setContactReason] = useState(reason);
  const [message, setMessage] = useState('');

  const selectReason = (contactReason: ContactReason) => {
    setContactReason(contactReason);
  };

  const changeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const sendMessage = (e: React.MouseEvent) => {
    const browserApi = getBrowserApi(window);
    e.nativeEvent.stopImmediatePropagation();
    setSendDisabled(true);

    const req: PlanChangeRequest = {
      orgId,
      subject: contactReason.name,
      message: message.trim(),
      sessionUrl: browserApi?.getCurrentSessionURL?.() ?? '',
    };

    xhr
      ?.PostJson(REQUEST_PLAN_CHANGE_ENDPOINT)
      .Success((status, rsp: any) => {
        setSendDisabled(false);
        onSend();
      })
      .Fail((status, rsp: any) => {
        // TODO: Make a notification place for React.
        if (status == 429) {
          notifications?.error(
            intl.formatMessage({
              defaultMessage: 'Contact rate limit exceeded, please try again in a few minutes.',
              id: 'aZ3foW',
            }),
          );
        } else {
          notifications?.error(
            intl.formatMessage({
              defaultMessage:
                'There was an error sending your message. Consider emailing support@fullstory.com directly.',
              id: 'nCOIPp',
            }),
          );
        }

        setSendDisabled(false);
      })
      .Send(req);
  };

  const sendMessageButton = (
    <Box marginBottom={16}>
      <Flex alignItems="center">
        <Button variant="fill-primary" onClick={sendMessage} disabled={sendDisabled}>
          <FormattedMessage defaultMessage="Send message" id="Xx0WZV" />
        </Button>
      </Flex>
    </Box>
  );

  return (
    <div data-component="ContactUs" data-testid="contact-us-form">
      <FormGroup>
        <Box marginBottom={4}>
          <Typography variant="body2">
            <FormattedMessage defaultMessage="Choose one" id="TFtxpy" />
          </Typography>
        </Box>
        <DeprecatedSelect
          variant="outline"
          value={contactReason}
          options={reasons.filter(
            value => !settings || !settings.plan || value.includeReason(settings.plan),
          )}
          optionId={r => r.name}
          optionText={r => r.name}
          onSelect={selectReason}
          hideSearch
          className={css.textN10}
        />
      </FormGroup>
      <FormGroup>
        <Box marginBottom={4}>
          <Typography variant="body2">
            <FormattedMessage defaultMessage="Message (Optional)" id="ULvt15" />
          </Typography>
        </Box>
        <TextArea
          rows={6}
          placeholder={
            isUpgradePage
              ? intl.formatMessage({
                  defaultMessage: 'Provide any additional details.',
                  id: 'FZzDfL',
                })
              : intl.formatMessage({
                  defaultMessage: "Tell us what's on your mind...",
                  id: 'cunQXt',
                })
          }
          value={message || ''}
          onChange={changeMessage}
        />
      </FormGroup>
      <Box marginBottom={32}>
        {isUpgradePage ? (
          sendMessageButton
        ) : (
          <Footer freePath={freePath || ''}>{sendMessageButton}</Footer>
        )}
      </Box>
    </div>
  );
};

const FormGroup: React.FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Box data-component="FormGroup" marginBottom={24}>
    {children}
  </Box>
);

interface ContactHeaderProps {
  isUpgradePage?: boolean;
}

const ContactHeader: React.FunctionComponent<ContactHeaderProps> = ({ isUpgradePage = false }) => {
  const stage = useSelector((state: AppState) => state.fullstory.orgStage);
  return isUpgradePage ? (
    <Text color="emphasized" appearance="title1">
      <FormattedMessage defaultMessage="Contact Us" id="hZOGLS" />
    </Text>
  ) : (
    <Typography color="neutrals-n11" variant="title1">
      {stage == PlanStage.CANCELLED_STAGE ? (
        <FormattedMessage defaultMessage="This account is not active." id="7f5gh8" />
      ) : (
        <FormattedMessage defaultMessage="How can we help you today?" id="WMt8r4" />
      )}
    </Typography>
  );
};

export { ContactUs, ContactHeader, ReasonId };
export type { ContactReason };
